import React from "react";
import Modal from "../../components/common/Modal/Modal";
import styles from "./LeadNotesModal.module.css";

// Keys written by the investment lead capture flow (crudFactory.js appApply /
// appLeadCapture), in the order they should be displayed. Anything present in
// the JSON but not listed here still renders, appended at the end — so a
// future field never silently disappears.
const FIELD_LABELS = [
  ["lead_status", "Lead Status"],
  ["source", "Data Source"],
  ["full_name", "Full Name"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["pan", "PAN"],
  ["upi", "UPI ID"],
  ["has_demat", "Has Demat Account"],
  ["accepted_tnc", "Accepted T&C"],
  ["item_name", "Item"],
  ["service_id", "Service ID"],
  ["service_type", "Service Type"],
  ["draft_id", "Draft ID"],
  ["captured_at", "Captured At"],
];

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (key === "captured_at") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
  }
  if (key === "lead_status") return value === "submitted" ? "Submitted" : "Canceled / Abandoned";
  if (key === "source") return value === "profile" ? "Auto-filled from profile" : "Entered by user";
  return String(value);
};

/**
 * Renders grievances.lead_notes for one row.
 *
 * lead_notes is a free-form JSON string written by the investment apply /
 * lead-capture endpoints (see crudFactory.js). This only ever reads it —
 * nothing here can write back to the row — so it can't affect any existing
 * ticket workflow.
 */
const LeadNotesModal = ({ isOpen, onClose, rawNotes }) => {
  let parsed = null;
  let parseFailed = false;

  if (rawNotes) {
    try {
      const value = JSON.parse(rawNotes);
      if (value && typeof value === "object" && !Array.isArray(value)) {
        parsed = value;
      } else {
        parseFailed = true;
      }
    } catch {
      parseFailed = true;
    }
  }

  const knownKeys = new Set(FIELD_LABELS.map(([key]) => key));
  const extraEntries = parsed
    ? Object.entries(parsed).filter(([key]) => !knownKeys.has(key))
    : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Lead Details" size="medium" className={styles.modalWidth}>
      {!rawNotes && (
        <p className={styles.emptyMessage}>No notes were recorded for this ticket.</p>
      )}

      {rawNotes && parseFailed && (
        <>
          <p className={styles.emptyMessage}>
            Notes are stored as plain text (not the structured lead format):
          </p>
          <pre className={styles.rawText}>{rawNotes}</pre>
        </>
      )}

      {parsed && (
        <div className={styles.grid}>
          {FIELD_LABELS.map(([key, label]) => (
            <div className={styles.row} key={key}>
              <span className={styles.label}>{label}</span>
              <span className={styles.value}>{formatValue(key, parsed[key])}</span>
            </div>
          ))}
          {extraEntries.map(([key, value]) => (
            <div className={styles.row} key={key}>
              <span className={styles.label}>{key}</span>
              <span className={styles.value}>{formatValue(key, value)}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default LeadNotesModal;
